// apps/web/src/pages/Admin/AdminQuizPools.js
// Admin → Quiz Pools: pick a movie or one TV season, prepare a private draft
// (manual or AI), preview it, and publish an approved pool — including early,
// inside the normal waiting period (docs/quiz-pools.md). Withdraw a live
// question or unpublish the pool if content turns out to be wrong.
import React, { useCallback, useState } from 'react';
import adminApi from '../../services/adminApi';
import styles from './AdminQuizPools.module.css';

const LETTERS = ['A', 'B', 'C', 'D'];

const blankQuestion = (target) => ({
    key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    question: '',
    options: ['', '', '', ''],
    correctIndex: null,
    origin: 'manual',
    reviewed: true,
    sourceNote: '',
    reviewerNote: '',
    target,
});

function formatDate(value) {
    if (!value) return null;
    const d = new Date(String(value).length === 10 ? `${value}T00:00:00Z` : value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

const RELEASE_LABELS = {
    released: 'Released',
    not_released: 'Not released yet',
    not_aired: 'Season has not aired yet',
    airing: 'Season still airing',
    complete: 'Season complete',
};

// ── Target picker ────────────────────────────────────────────────────────────
function TargetPicker({ onSelect }) {
    const [kind, setKind] = useState('movie');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [show, setShow] = useState(null);
    const [seasons, setSeasons] = useState([]);
    const [error, setError] = useState(null);

    async function search(e) {
        e.preventDefault();
        setError(null);
        setShow(null);
        try {
            const data = await adminApi.searchQuizPoolTitles(query, kind);
            setResults(data.results || []);
        } catch (err) {
            setError(err?.message || 'Search failed.');
        }
    }

    async function pick(r) {
        if (r.kind === 'movie') {
            onSelect({ tmdbId: r.tmdbId, kind: 'movie' }, r.title);
            return;
        }
        setShow(r);
        try {
            const data = await adminApi.listQuizPoolSeasons(r.tmdbId);
            setSeasons(data.seasons || []);
        } catch (err) {
            setError(err?.message || 'Could not load seasons.');
        }
    }

    return (
        <section className={styles.card}>
            <h2>Choose a movie or TV season</h2>
            <form className={styles.row} onSubmit={search}>
                <div role="radiogroup" aria-label="Type" className={styles.row}>
                    {['movie', 'tv'].map((k) => (
                        <label key={k} className={styles.radio}>
                            <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} />
                            {k === 'movie' ? 'Movie' : 'TV season'}
                        </label>
                    ))}
                </div>
                <input
                    className={styles.input}
                    aria-label="Search titles"
                    placeholder="Search TMDB by title…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                <button type="submit" disabled={query.trim().length < 2}>Search</button>
            </form>
            {error && <p className={styles.error}>{error}</p>}
            {!show && results.length > 0 && (
                <ul className={styles.results}>
                    {results.map((r) => (
                        <li key={`${r.kind}:${r.tmdbId}`}>
                            <button type="button" className={styles.linkBtn} onClick={() => pick(r)}>
                                {r.title}{r.year ? ` (${r.year})` : ''}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {show && (
                <div>
                    <p className={styles.muted}>{show.title}: pick a season</p>
                    <ul className={styles.results}>
                        {seasons.map((s) => (
                            <li key={s.seasonNumber}>
                                <button
                                    type="button"
                                    className={styles.linkBtn}
                                    onClick={() => onSelect({ tmdbId: show.tmdbId, kind: 'tv', seasonNumber: s.seasonNumber }, `${show.title} — ${s.name}`)}
                                >
                                    {s.name}{s.airDate ? ` · premiered ${formatDate(s.airDate)}` : ''}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

// ── Status / publication panel ───────────────────────────────────────────────
function StatusPanel({ data }) {
    const { status, pool, approvedPool } = data;
    const live = data.liveQuestions.filter((q) => q.active).length;
    return (
        <section className={styles.card} aria-label="Publication status">
            <h2>{status.label}</h2>
            <dl className={styles.facts}>
                <dt>Release</dt>
                <dd>
                    {RELEASE_LABELS[status.releaseState] ?? status.releaseState}
                    {status.releaseDate && status.target.kind === 'movie' ? ` · ${formatDate(status.releaseDate)}` : ''}
                    {status.finaleDate ? ` · finale ${formatDate(status.finaleDate)}` : ''}
                </dd>
                <dt>Normal availability</dt>
                <dd>
                    {status.normalAvailableAtFormatted
                        ? `${status.normalAvailableAtFormatted} (00:00 UTC, ${status.waitingPeriodDays} days after ${status.target.kind === 'tv' ? 'the finale' : 'release'})`
                        : 'Unknown until the release / finale date is known'}
                </dd>
                <dt>Early publication</dt>
                <dd>
                    {status.eligibleForEarlyPublication
                        ? 'Eligible — publishing now makes the quiz available before the normal date.'
                        : status.canPublish
                            ? 'Past the waiting period — publishing replaces the live pool.'
                            : `Not eligible yet — the ${status.target.kind === 'tv' ? 'season must finish airing' : 'movie must be released'} first. You can still prepare a draft.`}
                </dd>
                <dt>Players see</dt>
                <dd>
                    {status.publicAvailability.quizReady
                        ? (status.publicAvailability.earlyAccess ? 'Quiz available (early access)' : 'Quiz available')
                        : `Locked (${status.publicAvailability.reason})`}
                </dd>
                <dt>Published pool</dt>
                <dd>
                    {pool?.status === 'published'
                        ? `Version ${pool.publishedVersion}, published ${formatDate(pool.publishedAt)}${pool.publishedEarly ? ' (early)' : ''} · ${approvedPool?.playableCount ?? 0} active question(s)`
                        : pool?.status === 'unpublished'
                            ? `Unpublished (version ${pool.publishedVersion})`
                            : 'Not published'}
                </dd>
                <dt>Live questions</dt>
                <dd>{live}</dd>
            </dl>
            <p className={styles.notice}>Saving a draft does not make this quiz available.</p>
        </section>
    );
}

// ── One draft question editor ────────────────────────────────────────────────
function QuestionEditor({ q, index, count, errors, onChange, onRemove, onMove }) {
    const set = (patch) => onChange({ ...q, ...patch, ...(q.withdrawn && (patch.question !== undefined || patch.options) ? { withdrawn: false } : {}) });
    return (
        <li className={styles.question} aria-label={`Question ${index + 1}`}>
            <div className={styles.row}>
                <strong>Q{index + 1}</strong>
                {q.origin === 'ai' && <span className={q.reviewed ? styles.badge : styles.badgeWarn}>{q.reviewed ? 'AI · reviewed' : 'AI · needs review'}</span>}
                {q.withdrawn && <span className={styles.badgeWarn}>Withdrawn</span>}
                <span className={styles.spacer} />
                <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} aria-label={`Move question ${index + 1} up`}>↑</button>
                <button type="button" onClick={() => onMove(index, 1)} disabled={index === count - 1} aria-label={`Move question ${index + 1} down`}>↓</button>
                <button type="button" className={styles.danger} onClick={() => onRemove(index)}>Remove</button>
            </div>
            <textarea
                className={styles.input}
                aria-label={`Question ${index + 1} text`}
                value={q.question}
                onChange={(e) => set({ question: e.target.value })}
                rows={2}
            />
            <div className={styles.options}>
                {q.options.map((o, i) => (
                    <label key={i} className={styles.option}>
                        <input
                            type="radio"
                            name={`correct-${q.key}`}
                            checked={q.correctIndex === i}
                            onChange={() => set({ correctIndex: i })}
                            aria-label={`Question ${index + 1} correct answer ${LETTERS[i]}`}
                        />
                        <span>{LETTERS[i]}</span>
                        <input
                            className={styles.input}
                            aria-label={`Question ${index + 1} choice ${LETTERS[i]}`}
                            value={o}
                            onChange={(e) => set({ options: q.options.map((x, j) => (j === i ? e.target.value : x)) })}
                        />
                    </label>
                ))}
            </div>
            <details>
                <summary>Internal notes (never shown to players)</summary>
                <textarea className={styles.input} aria-label={`Question ${index + 1} source note`} placeholder="Source (episode, scene, link)…" value={q.sourceNote} onChange={(e) => set({ sourceNote: e.target.value })} rows={2} />
                <textarea className={styles.input} aria-label={`Question ${index + 1} reviewer note`} placeholder="Reviewer note…" value={q.reviewerNote} onChange={(e) => set({ reviewerNote: e.target.value })} rows={2} />
            </details>
            {q.origin === 'ai' && (
                <label className={styles.radio}>
                    <input type="checkbox" checked={q.reviewed} onChange={(e) => set({ reviewed: e.target.checked })} />
                    I checked this AI question against a reliable source
                </label>
            )}
            {errors?.length > 0 && (
                <ul className={styles.errors}>{errors.map((e) => <li key={e}>{e}</li>)}</ul>
            )}
        </li>
    );
}

// ── Preview ──────────────────────────────────────────────────────────────────
function Preview({ questions, spoilerLine, onClose }) {
    const [i, setI] = useState(0);
    const q = questions[i];
    return (
        <section className={styles.card} aria-label="Quiz preview">
            <div className={styles.row}>
                <h2>Preview</h2>
                <span className={styles.spacer} />
                <button type="button" onClick={onClose}>Close preview</button>
            </div>
            <p className={styles.notice}>{spoilerLine}</p>
            {q ? (
                <div className={styles.previewCard}>
                    <p className={styles.muted}>Question {i + 1} of {questions.length} (players get 8 per attempt)</p>
                    <p className={styles.previewQ}>{q.question || <em>(no text)</em>}</p>
                    <ol className={styles.previewOptions}>
                        {q.options.map((o, j) => <li key={j} className={j === q.correctIndex ? styles.previewCorrect : ''}>{o || <em>(empty)</em>}</li>)}
                    </ol>
                    <div className={styles.row}>
                        <button type="button" onClick={() => setI(i - 1)} disabled={i === 0}>Previous</button>
                        <button type="button" onClick={() => setI(i + 1)} disabled={i === questions.length - 1}>Next</button>
                    </div>
                </div>
            ) : <p className={styles.muted}>No questions yet.</p>}
        </section>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function AdminQuizPools() {
    const [target, setTarget] = useState(null);
    const [data, setData] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [notes, setNotes] = useState('');
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(null);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [publishErrors, setPublishErrors] = useState(null);
    const [previewing, setPreviewing] = useState(false);

    const apply = useCallback((next) => {
        setData(next);
        setQuestions(next.draft.questions);
        setNotes(next.draft.notes || '');
        setDirty(false);
        setPublishErrors(null);
    }, []);

    const run = async (label, fn, successText) => {
        setBusy(label);
        setError(null);
        setMessage(null);
        try {
            const next = await fn();
            if (next) apply(next);
            if (successText) setMessage(typeof successText === 'function' ? successText(next) : successText);
        } catch (err) {
            setError(err?.message || 'Something went wrong.');
            if (err?.payload?.details?.questionErrors || err?.payload?.details?.errors) setPublishErrors(err.payload.details);
        } finally {
            setBusy(null);
        }
    };

    const select = (t) => {
        setTarget(t);
        setPreviewing(false);
        run('load', () => adminApi.getQuizPool(t));
    };

    const edit = (updater) => { setQuestions(updater); setDirty(true); };
    const revision = data?.pool?.draftRevision;

    const save = () => run('save', () => adminApi.saveQuizPoolDraft(target, { questions, notes, baseRevision: revision }), 'Draft saved. It is not visible to players.');
    const generate = () => {
        if (dirty && !window.confirm('Save your edits first? Unsaved changes will be lost when the AI draft is added.')) return;
        run('generate', () => adminApi.generateQuizPoolDraft(target, revision),
            (next) => `Added ${next.generatedCount} AI question(s) to the draft. Review each one before publishing.`);
    };
    const publish = () => {
        const s = data.status;
        const early = s.eligibleForEarlyPublication;
        const text = early
            ? `Publish quiz early for ${s.label}?\n\nUsers can access this quiz immediately, before the normal date (${s.normalAvailableAtFormatted}).`
            : `Publish this quiz pool for ${s.label}?\n\nIt replaces the live questions immediately.`;
        if (!window.confirm(text)) return;
        run('publish', () => adminApi.publishQuizPool(target, revision), early ? 'Published early. The quiz is available now.' : 'Published.');
    };
    const unpublish = () => {
        if (!window.confirm(`Unpublish the quiz pool for ${data.status.label}? New attempts stop using it immediately.`)) return;
        run('unpublish', () => adminApi.unpublishQuizPool(target), 'Unpublished.');
    };
    const withdraw = (q) => {
        const reason = window.prompt(`Withdraw this question?\n\n"${q.question}"\n\nReason (internal):`);
        if (!reason || !reason.trim()) return;
        run('withdraw', () => adminApi.withdrawQuizPoolQuestion(q.id, reason.trim()), 'Question withdrawn. New attempts will not receive it.');
    };

    const validation = !dirty && data ? data.validation : null;
    const questionErrors = publishErrors?.questionErrors || validation?.questionErrors || {};
    const poolErrors = publishErrors?.errors || validation?.errors || [];
    const limits = data?.limits;
    const canPublishNow = data?.status?.canPublish && !dirty && validation?.valid;
    const spoilerLine = target?.kind === 'tv' ? `Contains spoilers for Season ${target.seasonNumber}.` : 'Contains spoilers for this movie.';

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <h1>Quiz Pools</h1>
                <p className={styles.muted}>
                    Prepare and approve a complete question pool for a movie or one TV season. Quizzes normally
                    unlock automatically 7 days after a movie's release and 3 days after a season finale; an approved
                    pool can be published as soon as the movie is released or the season has finished airing.
                </p>
            </header>

            <TargetPicker onSelect={select} />

            {busy === 'load' && <p className={styles.muted}>Loading…</p>}
            {error && <p className={styles.error} role="alert">{error}</p>}
            {message && <p className={styles.success} role="status">{message}</p>}

            {data && target && (
                <>
                    <StatusPanel data={data} />

                    {previewing && <Preview questions={questions} spoilerLine={spoilerLine} onClose={() => setPreviewing(false)} />}

                    <section className={styles.card} aria-label="Draft">
                        <div className={styles.row}>
                            <h2>Draft ({questions.length}/{limits.publishSize})</h2>
                            <span className={styles.spacer} />
                            {dirty && <span className={styles.badgeWarn}>Unsaved changes</span>}
                        </div>
                        <p className={styles.muted}>
                            A published pool needs exactly {limits.publishSize} questions, each with {limits.optionCount} choices and one correct
                            answer. Players get {limits.attemptSize} per attempt. Drafts can be saved incomplete.
                        </p>
                        {poolErrors.length > 0 && <ul className={styles.errors}>{poolErrors.map((e) => <li key={e}>{e}</li>)}</ul>}
                        <ol className={styles.questions}>
                            {questions.map((q, i) => (
                                <QuestionEditor
                                    key={q.key}
                                    q={q}
                                    index={i}
                                    count={questions.length}
                                    errors={questionErrors[q.key]}
                                    onChange={(next) => edit((qs) => qs.map((x, j) => (j === i ? next : x)))}
                                    onRemove={(idx) => edit((qs) => qs.filter((_, j) => j !== idx))}
                                    onMove={(idx, d) => edit((qs) => {
                                        const copy = [...qs];
                                        [copy[idx], copy[idx + d]] = [copy[idx + d], copy[idx]];
                                        return copy;
                                    })}
                                />
                            ))}
                        </ol>
                        <label className={styles.label}>
                            Pool notes (internal)
                            <textarea className={styles.input} aria-label="Pool notes" value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }} rows={2} />
                        </label>
                        <div className={styles.row}>
                            <button type="button" onClick={() => edit((qs) => [...qs, blankQuestion(target)])} disabled={questions.length >= limits.maxDraftSize}>Add question</button>
                            <button type="button" onClick={generate} disabled={!!busy || !data.status.released}>
                                {busy === 'generate' ? 'Generating…' : 'Generate draft questions (AI)'}
                            </button>
                            <button type="button" onClick={() => setPreviewing(true)}>Preview quiz</button>
                            <span className={styles.spacer} />
                            <button type="button" onClick={save} disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save draft'}</button>
                        </div>
                        {!data.status.released && (
                            <p className={styles.muted}>AI generation is available once the {target.kind === 'tv' ? 'season has finished airing' : 'movie is released'}; write questions manually until then.</p>
                        )}
                    </section>

                    <section className={styles.card} aria-label="Publish">
                        <h2>Publish</h2>
                        <p className={styles.notice}>Saving a draft does not make this quiz available.</p>
                        {!data.status.canPublish && (
                            <p className={styles.muted}>Publishing is blocked until the {target.kind === 'tv' ? 'season finale has aired' : 'release date'}.</p>
                        )}
                        {dirty && <p className={styles.muted}>Save the draft before publishing.</p>}
                        <div className={styles.row}>
                            <button type="button" onClick={publish} disabled={!canPublishNow || !!busy}>
                                {data.status.eligibleForEarlyPublication ? 'Publish quiz early' : 'Publish quiz'}
                            </button>
                            {data.pool?.status === 'published' && (
                                <button type="button" className={styles.danger} onClick={unpublish} disabled={!!busy}>Unpublish</button>
                            )}
                        </div>
                    </section>

                    <section className={styles.card} aria-label="Live questions">
                        <h2>Live questions</h2>
                        {data.liveQuestions.filter((q) => q.active).length === 0 && <p className={styles.muted}>No live questions.</p>}
                        <ul className={styles.live}>
                            {data.liveQuestions.filter((q) => q.active).map((q) => (
                                <li key={q.id}>
                                    <span>{q.question}</span>
                                    <span className={styles.muted}> — {q.options[q.correctIndex]} {q.poolVersion ? `(v${q.poolVersion})` : '(automatic)'}</span>
                                    <button type="button" className={styles.danger} onClick={() => withdraw(q)} disabled={!!busy}>Withdraw</button>
                                </li>
                            ))}
                        </ul>
                    </section>
                </>
            )}
        </div>
    );
}
